import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: 2,
            p: 4,
            textAlign: "center",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 56, color: "error.main" }} />
          <Typography variant="h6" fontWeight={600}>
            Something went wrong
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 400 }}
          >
            An unexpected error occurred. Refresh the page or go back to
            continue.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.history.back();
              }}
            >
              Go back
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
